import type { SVGProps } from "react";

export const ICON_NAMES = [
  "account",
  "alert",
  "arrow",
  "arrowBack",
  "bell",
  "book",
  "bookmark",
  "calendar",
  "chevron",
  "check",
  "circleCheck",
  "close",
  "compass",
  "download",
  "edit",
  "expand",
  "eye",
  "flask",
  "gift",
  "grid",
  "heart",
  "history",
  "image",
  "info",
  "lightbulb",
  "link",
  "lock",
  "menu",
  "mic",
  "more",
  "palette",
  "paperclip",
  "pause",
  "play",
  "plus",
  "printer",
  "quote",
  "record",
  "search",
  "settings",
  "share",
  "shield",
  "sound",
  "soundOff",
  "spark",
  "sprout",
  "stop",
  "trash",
  "upload",
  "userPlus",
  "users",
] as const;

export type IconName = (typeof ICON_NAMES)[number];

export const LC93_AUTHORITY_ICON_IDENTITIES = [
  "alert",
  "arrow-right",
  "book-open",
  "check",
  "close",
  "family",
  "gift",
  "lock",
  "microphone",
  "more",
  "photograph",
  "review",
  "search",
  "settings",
] as const;

export type Lc93AuthorityIconIdentity = (typeof LC93_AUTHORITY_ICON_IDENTITIES)[number];

export type IconGovernance = Readonly<{
  classification: "mapped-authority-identity-candidate" | "extension-candidate";
  authorityIdentity: Lc93AuthorityIconIdentity | null;
  exactAuthority: false;
  source: "inline-candidate";
}>;

/**
 * Governance is deliberately separate from rendering. These paths pre-date the
 * sealed external package and remain candidates even when their semantic name
 * maps to one of the fourteen approved authority identities.
 */
export const ICON_GOVERNANCE = {
  account: { authorityIdentity: null, classification: "extension-candidate" },
  alert: { authorityIdentity: "alert", classification: "mapped-authority-identity-candidate" },
  arrow: {
    authorityIdentity: "arrow-right",
    classification: "mapped-authority-identity-candidate",
  },
  arrowBack: { authorityIdentity: null, classification: "extension-candidate" },
  bell: { authorityIdentity: null, classification: "extension-candidate" },
  book: {
    authorityIdentity: "book-open",
    classification: "mapped-authority-identity-candidate",
  },
  bookmark: { authorityIdentity: null, classification: "extension-candidate" },
  calendar: { authorityIdentity: null, classification: "extension-candidate" },
  chevron: { authorityIdentity: null, classification: "extension-candidate" },
  check: { authorityIdentity: "check", classification: "mapped-authority-identity-candidate" },
  circleCheck: { authorityIdentity: null, classification: "extension-candidate" },
  close: { authorityIdentity: "close", classification: "mapped-authority-identity-candidate" },
  compass: { authorityIdentity: null, classification: "extension-candidate" },
  download: { authorityIdentity: null, classification: "extension-candidate" },
  edit: { authorityIdentity: null, classification: "extension-candidate" },
  expand: { authorityIdentity: null, classification: "extension-candidate" },
  eye: { authorityIdentity: null, classification: "extension-candidate" },
  flask: { authorityIdentity: null, classification: "extension-candidate" },
  gift: { authorityIdentity: "gift", classification: "mapped-authority-identity-candidate" },
  grid: { authorityIdentity: null, classification: "extension-candidate" },
  heart: { authorityIdentity: null, classification: "extension-candidate" },
  history: { authorityIdentity: null, classification: "extension-candidate" },
  image: {
    authorityIdentity: "photograph",
    classification: "mapped-authority-identity-candidate",
  },
  info: { authorityIdentity: null, classification: "extension-candidate" },
  lightbulb: { authorityIdentity: null, classification: "extension-candidate" },
  link: { authorityIdentity: null, classification: "extension-candidate" },
  lock: { authorityIdentity: "lock", classification: "mapped-authority-identity-candidate" },
  menu: { authorityIdentity: null, classification: "extension-candidate" },
  mic: {
    authorityIdentity: "microphone",
    classification: "mapped-authority-identity-candidate",
  },
  more: { authorityIdentity: "more", classification: "mapped-authority-identity-candidate" },
  palette: { authorityIdentity: null, classification: "extension-candidate" },
  paperclip: { authorityIdentity: null, classification: "extension-candidate" },
  pause: { authorityIdentity: null, classification: "extension-candidate" },
  play: { authorityIdentity: null, classification: "extension-candidate" },
  plus: { authorityIdentity: null, classification: "extension-candidate" },
  printer: { authorityIdentity: null, classification: "extension-candidate" },
  quote: { authorityIdentity: null, classification: "extension-candidate" },
  record: { authorityIdentity: null, classification: "extension-candidate" },
  search: { authorityIdentity: "search", classification: "mapped-authority-identity-candidate" },
  settings: {
    authorityIdentity: "settings",
    classification: "mapped-authority-identity-candidate",
  },
  share: { authorityIdentity: null, classification: "extension-candidate" },
  shield: { authorityIdentity: null, classification: "extension-candidate" },
  sound: { authorityIdentity: null, classification: "extension-candidate" },
  soundOff: { authorityIdentity: null, classification: "extension-candidate" },
  spark: { authorityIdentity: null, classification: "extension-candidate" },
  sprout: { authorityIdentity: null, classification: "extension-candidate" },
  stop: { authorityIdentity: null, classification: "extension-candidate" },
  trash: { authorityIdentity: null, classification: "extension-candidate" },
  upload: { authorityIdentity: null, classification: "extension-candidate" },
  userPlus: { authorityIdentity: null, classification: "extension-candidate" },
  users: { authorityIdentity: "family", classification: "mapped-authority-identity-candidate" },
} as const satisfies Record<IconName, Pick<IconGovernance, "authorityIdentity" | "classification">>;

export function getIconGovernance(name: IconName): IconGovernance {
  return { ...ICON_GOVERNANCE[name], exactAuthority: false, source: "inline-candidate" };
}

const paths: Record<IconName, React.ReactNode> = {
  account: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 22a8 8 0 0 1 16 0" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3 2.8 20h18.4Z" />
      <path d="M12 8v5M12 17h.01" />
    </>
  ),
  arrow: (
    <>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </>
  ),
  arrowBack: (
    <>
      <path d="M19 12H5" />
      <path d="m11 6-6 6 6 6" />
    </>
  ),
  book: (
    <>
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v17H6.5A2.5 2.5 0 0 0 4 22.5Z" />
      <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v17h4.5a2.5 2.5 0 0 1 2.5 2.5Z" />
    </>
  ),
  bookmark: <path d="M6 3h12v18l-6-4-6 4Z" />,
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M10 21h4" />
    </>
  ),
  chevron: <path d="m8 10 4 4 4-4" />,
  check: <path d="m5 12 4 4L19 6" />,
  circleCheck: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 2.5 2.5L16.5 8" />
    </>
  ),
  close: (
    <>
      <path d="m6 6 12 12" />
      <path d="M18 6 6 18" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m15.5 8.5-2 5-5 2 2-5Z" />
    </>
  ),
  download: (
    <>
      <path d="M12 3v12M7 10l5 5 5-5" />
      <path d="M4 20h16" />
    </>
  ),
  edit: (
    <>
      <path d="m14.5 5.5 4 4L9 19l-5 1 1-5Z" />
      <path d="m12.5 7.5 4 4" />
    </>
  ),
  expand: (
    <>
      <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M21 16v5h-5" />
      <path d="m3 8 6-6M21 8l-6-6M3 16l6 6M21 16l-6 6" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  flask: (
    <>
      <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3l-5-9V3" />
      <path d="M7.5 16h9" />
    </>
  ),
  gift: (
    <>
      <rect x="3" y="8" width="18" height="13" rx="1" />
      <path d="M12 8v13M3 12h18M12 8H8.5A2.5 2.5 0 1 1 11 5.5ZM12 8h3.5A2.5 2.5 0 1 0 13 5.5Z" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  heart: (
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9" r="1.5" />
      <path d="m4 17 5-5 3 3 2-2 6 6" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5M12 7v5l3 2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7h.01" />
    </>
  ),
  lightbulb: (
    <>
      <path d="M9 18h6M10 22h4" />
      <path d="M8.5 15.5A7 7 0 1 1 15.5 15.5L15 18H9Z" />
    </>
  ),
  link: (
    <>
      <path d="m10 13.5 4-4" />
      <path d="M7.5 16.5 6 18a3.5 3.5 0 0 1-5-5l3-3a3.5 3.5 0 0 1 5 0" />
      <path d="M16.5 7.5 18 6a3.5 3.5 0 0 1 5 5l-3 3a3.5 3.5 0 0 1-5 0" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="14" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0M12 17v5M8 22h8" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  palette: (
    <>
      <path d="M12 3a9 9 0 1 0 0 18h1.5a2 2 0 0 0 0-4H12a1.5 1.5 0 0 1 0-3h3a6 6 0 0 0 6-6c0-3-4-5-9-5Z" />
      <circle cx="7.5" cy="9" r=".8" fill="currentColor" stroke="none" />
      <circle cx="11" cy="6.5" r=".8" fill="currentColor" stroke="none" />
      <circle cx="15" cy="7" r=".8" fill="currentColor" stroke="none" />
    </>
  ),
  paperclip: <path d="m9 17 7.8-7.8a3 3 0 1 0-4.2-4.2l-8.5 8.5a5 5 0 0 0 7.1 7.1L20 12" />,
  pause: (
    <>
      <path d="M9 5v14" />
      <path d="M15 5v14" />
    </>
  ),
  play: <path d="m8 5 11 7-11 7Z" />,
  plus: (
    <>
      <path d="M12 5v14M5 12h14" />
    </>
  ),
  printer: (
    <>
      <path d="M7 9V3h10v6M7 17H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-3" />
      <rect x="7" y="14" width="10" height="7" />
    </>
  ),
  quote: (
    <>
      <path d="M9 11H5a4 4 0 0 0 4 4v2a6 6 0 0 1-6-6V5h6Z" />
      <path d="M21 11h-4a4 4 0 0 0 4 4v2a6 6 0 0 1-6-6V5h6Z" />
    </>
  ),
  record: <circle cx="12" cy="12" r="6" fill="currentColor" stroke="none" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 5 5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" />
    </>
  ),
  share: (
    <>
      <circle cx="18" cy="5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="19" r="2.5" />
      <path d="m8.2 10.8 7.6-4.6M8.2 13.2l7.6 4.6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  sound: (
    <>
      <path d="M11 5 6.5 9H3v6h3.5L11 19Z" />
      <path d="M15 9a4 4 0 0 1 0 6M17.8 6.8a7.3 7.3 0 0 1 0 10.4" />
    </>
  ),
  soundOff: (
    <>
      <path d="M11 5 6.5 9H3v6h3.5L11 19Z" />
      <path d="m16 10 5 5M21 10l-5 5" />
    </>
  ),
  spark: (
    <>
      <path d="m12 3-1 4a5 5 0 0 1-4 4l-4 1 4 1a5 5 0 0 1 4 4l1 4 1-4a5 5 0 0 1 4-4l4-1-4-1a5 5 0 0 1-4-4Z" />
    </>
  ),
  sprout: (
    <>
      <path d="M12 21V10" />
      <path d="M12 13C7 13 4 10 4 5c5 0 8 3 8 8ZM12 16c0-5 3-8 8-8 0 5-3 8-8 8Z" />
    </>
  ),
  stop: <rect x="6" y="6" width="12" height="12" rx="1" fill="currentColor" stroke="none" />,
  trash: (
    <>
      <path d="M4 7h16M9 3h6l1 4M6 7l1 14h10l1-14M10 11v6M14 11v6" />
    </>
  ),
  upload: (
    <>
      <path d="M12 17V5M7 10l5-5 5 5" />
      <path d="M4 20h16" />
    </>
  ),
  userPlus: (
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 22a7 7 0 0 1 14 0M19 8v6M16 11h6" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
    </>
  ),
};

export function Icon({
  name,
  size = 20,
  ...props
}: { name: IconName; size?: number } & SVGProps<SVGSVGElement>) {
  const governance = getIconGovernance(name);
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
      data-icon-name={name}
      data-icon-governance={governance.classification}
      data-authority-identity={governance.authorityIdentity ?? undefined}
      data-exact-authority="false"
    >
      {paths[name]}
    </svg>
  );
}
